/**
 * Unified equipment assets — Villager OR Vendor owned (UI mock).
 * Supports Available / Deployed operational status for indent matching.
 */

import { peopleMasterSeed } from './people';
import { villagerAssetSeed, vehicleAgeFromReg } from './villagerAssets';

export { vehicleAgeFromReg };

export const OWNER_TYPES = ['Villager', 'Vendor'];
export const OPERATIONAL_STATUSES = ['Available', 'Deployed'];
export const ASSET_KYC_STATUSES = ['Verified', 'Pending', 'Rejected'];
export const ASSET_KYC_OPTIONS = ASSET_KYC_STATUSES.map((s) => ({ value: s, label: s }));

function villageIdForPerson(peopleId) {
  return peopleMasterSeed.find((p) => p.peopleId === peopleId)?.villageId || '';
}

function defaultEquipmentDescription(asset) {
  return asset.equipmentDescription || asset.equipmentType || '';
}

/** Lift villager seed into unified shape. */
function fromVillagerSeed(a) {
  return {
    assetId: a.assetId,
    ownerType: 'Villager',
    peopleId: a.peopleId,
    vendorId: null,
    villageId: villageIdForPerson(a.peopleId),
    equipmentType: a.equipmentType,
    equipmentDescription: defaultEquipmentDescription(a),
    remarks: a.remarks || '',
    vehicleClass: a.vehicleClass,
    manufacturer: a.manufacturer,
    model: a.model,
    variant: a.variant || '',
    capacity: a.capacity || '',
    capacityUom: a.capacityUom || '',
    yom: a.yom || '',
    regNo: a.regNo,
    chassisNumber: a.chassisNumber,
    engineNumber: a.engineNumber,
    dateOfReg: a.dateOfReg,
    regValidUpto: a.regValidUpto,
    nameAsPerRc: a.nameAsPerRc,
    sonOfWifeOf: a.sonOfWifeOf || '',
    color: a.color || '',
    fuel: a.fuel || '',
    pucDate: a.pucDate,
    pucValidUpto: a.pucValidUpto,
    insuranceIssueDate: a.insuranceIssueDate,
    insuranceValidUpto: a.insuranceValidUpto,
    taxValidUpto: a.taxValidUpto,
    fitnessValidUpto: a.fitnessValidUpto,
    status: a.status || 'Pending',
    operationalStatus: 'Available',
    documents: a.documents || [],
  };
}

/** Vendor-owned fleet samples (Excel M_Equipment master style). */
const vendorAssetSeed = [
  {
    assetId: 'AST-V001',
    ownerType: 'Vendor',
    peopleId: null,
    vendorId: 'VND-001',
    villageId: 'V-MUN-002',
    equipmentType: 'Excavator',
    equipmentDescription: 'EXCAVATOR',
    remarks: '',
    vehicleClass: 'Construction Equipment',
    manufacturer: 'HYUNDAI',
    model: 'R210',
    variant: '',
    capacity: '1',
    capacityUom: 'CUM',
    yom: '2019',
    regNo: 'GJ12CM6802',
    chassisNumber: 'HYNDN633JE0068964',
    engineNumber: 'HYN210E001',
    dateOfReg: '2019-05-10',
    regValidUpto: '2034-05-09',
    nameAsPerRc: 'Mundra Earth Movers',
    sonOfWifeOf: '',
    color: 'Yellow',
    fuel: 'Diesel',
    pucDate: '2025-01-01',
    pucValidUpto: '2026-01-01',
    insuranceIssueDate: '2025-02-01',
    insuranceValidUpto: '2026-02-01',
    taxValidUpto: '2026-03-31',
    fitnessValidUpto: '2027-05-09',
    status: 'Verified',
    operationalStatus: 'Available',
    documents: [],
  },
  {
    assetId: 'AST-V002',
    ownerType: 'Vendor',
    peopleId: null,
    vendorId: 'VND-002',
    villageId: 'V-MUN-059',
    equipmentType: 'Crane',
    equipmentDescription: 'PICK & CARRY CRANE',
    remarks: '',
    vehicleClass: 'Construction Equipment',
    manufacturer: 'ESCORTS',
    model: 'TRX 1550',
    variant: 'Pick & Carry',
    capacity: '15',
    capacityUom: 'T',
    yom: '2018',
    regNo: 'GJ12CM8818',
    chassisNumber: 'ESC1550008818',
    engineNumber: 'ESC1550E88',
    dateOfReg: '2018-08-20',
    regValidUpto: '2033-08-19',
    nameAsPerRc: 'Kutch Hire Services',
    sonOfWifeOf: '',
    color: 'Yellow',
    fuel: 'Diesel',
    pucDate: '2025-03-01',
    pucValidUpto: '2026-03-01',
    insuranceIssueDate: '2025-03-10',
    insuranceValidUpto: '2026-03-09',
    taxValidUpto: 'LTT',
    fitnessValidUpto: '2026-08-19',
    status: 'Verified',
    operationalStatus: 'Available',
    documents: [],
  },
  {
    assetId: 'AST-V003',
    ownerType: 'Vendor',
    peopleId: null,
    vendorId: 'VND-002',
    villageId: 'V-MUN-059',
    equipmentType: 'Crane',
    equipmentDescription: 'PICK & CARRY CRANE',
    remarks: '',
    vehicleClass: 'Construction Equipment',
    manufacturer: 'ACE',
    model: 'FX150',
    variant: '',
    capacity: '15',
    capacityUom: 'T',
    yom: '2021',
    regNo: 'GJ12CN2201',
    chassisNumber: 'ACEFX15002201',
    engineNumber: 'ACE150E22',
    dateOfReg: '2021-04-12',
    regValidUpto: '2036-04-11',
    nameAsPerRc: 'Kutch Hire Services',
    sonOfWifeOf: '',
    color: 'Orange',
    fuel: 'Diesel',
    pucDate: '2025-04-01',
    pucValidUpto: '2026-04-01',
    insuranceIssueDate: '2025-04-15',
    insuranceValidUpto: '2026-04-14',
    taxValidUpto: '2026-04-11',
    fitnessValidUpto: '2027-04-11',
    status: 'Verified',
    operationalStatus: 'Available',
    documents: [],
  },
];

/** Extra villager crane for nearby matching demos. */
const extraVillagerCrane = {
  assetId: 'AST-006',
  ownerType: 'Villager',
  peopleId: 'P-003',
  vendorId: null,
  villageId: villageIdForPerson('P-003'),
  equipmentType: 'Crane',
  equipmentDescription: 'TYRE MOUNTED CRANE',
  remarks: '',
  vehicleClass: 'Construction Equipment',
  manufacturer: 'SANY',
  model: 'STC250',
  variant: 'Tyre Mounted',
  capacity: '25',
  capacityUom: 'T',
  yom: '2020',
  regNo: 'GJ12 CK 4411',
  chassisNumber: 'SANY250004411',
  engineNumber: 'SANY250E44',
  dateOfReg: '2020-09-01',
  regValidUpto: '2035-08-31',
  nameAsPerRc: 'Suresh Laxman Gadhvi',
  sonOfWifeOf: 'Laxman Gadhvi',
  color: 'White',
  fuel: 'Diesel',
  pucDate: '2025-05-01',
  pucValidUpto: '2026-04-30',
  insuranceIssueDate: '2025-05-10',
  insuranceValidUpto: '2026-05-09',
  taxValidUpto: '2026-08-31',
  fitnessValidUpto: '2027-08-31',
  status: 'Verified',
  operationalStatus: 'Available',
  documents: [],
};

export const equipmentAssetSeed = [
  ...villagerAssetSeed.map(fromVillagerSeed),
  extraVillagerCrane,
  ...vendorAssetSeed,
];
